import { useEffect, useRef, useState } from "react";
import { acceptOrgInviteSchema } from "@io-spoke/contracts/orgs";
import { useOrgContext } from "@components/layout/OrgContext";
import { useAsyncCallback } from "../hooks/useAsyncCallback";
import { acceptOrgInvite } from "../lib/orgInvites";
import { PageShell } from "@components/layout/PageShell";
import { Card, CardContent, CardHeader, CardTitle } from "@components/ui/card";
import { Button } from "@components/ui/button";

function invitationToken() {
  const values = new URLSearchParams(window.location.search).getAll("token");
  if (values.length !== 1) return null;
  const parsed = acceptOrgInviteSchema.safeParse({ token: values[0] });
  return parsed.success ? parsed.data.token : null;
}

export function OrgInvitePage({ navigate, onUseDifferentAccount }: { navigate: (path: string) => void; onUseDifferentAccount: () => void | Promise<void> }) {
  const { activateCreatedWorkspace } = useOrgContext();
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const [error, setError] = useState<string | null>(null);
  const currentToken = invitationToken();
  const token = useRef(currentToken);
  token.current = currentToken;
  useEffect(() => { setError(null); }, [currentToken]);
  const acceptance = useAsyncCallback(async () => {
    const submitted = token.current;
    if (!submitted) return;
    setError(null);
    try {
      const result = await acceptOrgInvite(submitted);
      if (!mounted.current || token.current !== submitted) return;
      activateCreatedWorkspace(result);
      const url = new URL(window.location.href);
      url.searchParams.delete("token");
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
      navigate("/obligations");
    } catch (cause) {
      if (mounted.current && token.current === submitted) setError(cause instanceof Error ? cause.message : "Unable to accept this invitation. Ask the administrator for a new link.");
    }
  });
  const changeAccount = useAsyncCallback(async () => { await onUseDifferentAccount(); });
  return <PageShell maxWidth="lg"><Card className="mt-10"><CardHeader><CardTitle>Join your workspace</CardTitle></CardHeader><CardContent className="space-y-5">
    <p className="text-sm text-muted-foreground">Accept this invitation using the email address it was sent to. You do not need to create another workspace.</p>
    {!currentToken ? <p role="alert" className="text-sm text-destructive">This invitation link is missing or invalid. Ask the workspace administrator for a new link.</p> : null}
    {error ? <p role="alert" className="text-sm text-destructive">{error}</p> : null}
    <Button onClick={() => void acceptance.callback()} disabled={!currentToken || changeAccount.pending} loading={acceptance.pending} loadingLabel="Joining workspace…">Accept invitation</Button>
    <Button variant="outline" onClick={() => void changeAccount.callback()} disabled={acceptance.pending} loading={changeAccount.pending} loadingLabel="Switching account…">Use a different account</Button>
  </CardContent></Card></PageShell>;
}
